import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { API, AuthContext } from '@/App';
import axios from 'axios';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const navigate = useNavigate();
  const { token } = React.useContext(AuthContext);
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        // Refresh page after 2 seconds to update user context
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 2000);
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FFE5EC 0%, #FFF8F3 100%)' }}>
      <Card className="glass max-w-md w-full" data-testid="payment-status-card">
        <CardHeader>
          <CardTitle className="text-center" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Payment Status
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          {status === 'checking' && (
            <div data-testid="payment-checking">
              <Loader2 className="w-16 h-16 mx-auto mb-4 text-rose-500 animate-spin" />
              <p className="text-lg mb-2">Processing your payment...</p>
              <p className="text-sm text-gray-600">Please wait while we confirm your membership upgrade.</p>
            </div>
          )}

          {status === 'success' && (
            <div data-testid="payment-success">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <h2 className="text-2xl font-bold mb-2 text-green-700">Payment Successful!</h2>
              <p className="text-gray-700 mb-4">Your membership has been upgraded successfully.</p>
              <p className="text-sm text-gray-600">Redirecting to dashboard...</p>
            </div>
          )}

          {status === 'expired' && (
            <div data-testid="payment-expired">
              <XCircle className="w-16 h-16 mx-auto mb-4 text-orange-500" />
              <h2 className="text-2xl font-bold mb-2 text-orange-700">Session Expired</h2>
              <p className="text-gray-700 mb-4">Your payment session has expired.</p>
              <Button onClick={() => navigate('/membership')} className="bg-rose-500 hover:bg-rose-600">
                Try Again
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div data-testid="payment-error">
              <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
              <h2 className="text-2xl font-bold mb-2 text-red-700">Payment Error</h2>
              <p className="text-gray-700 mb-4">There was an error processing your payment.</p>
              <Button onClick={() => navigate('/membership')} className="bg-rose-500 hover:bg-rose-600">
                Back to Membership
              </Button>
            </div>
          )}

          {status === 'timeout' && (
            <div data-testid="payment-timeout">
              <Loader2 className="w-16 h-16 mx-auto mb-4 text-gray-500" />
              <h2 className="text-2xl font-bold mb-2 text-gray-700">Checking Status...</h2>
              <p className="text-gray-700 mb-4">This is taking longer than expected. Please check your email for confirmation.</p>
              <Button onClick={() => navigate('/dashboard')} className="bg-rose-500 hover:bg-rose-600">
                Go to Dashboard
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;